import { useState } from 'react';
import { useShow, useTranslate } from '@refinedev/core';
import { Show, DateField } from '@refinedev/antd';
import { Typography, Divider, Descriptions, Space, Table, Button } from 'antd';
import { PlusOutlined, ForwardOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type {
  EntitlementDetailOut,
  LedgerEntryOut,
} from '@/api/generated/schemas';
import { EntitlementStatusTag } from '@/components/monetization/EntitlementStatusTag';
import { GrantEntitlementModal } from '@/components/monetization/GrantEntitlementModal';
import { LedgerEntryAddModal } from '@/components/monetization/LedgerEntryAddModal';

const { Title } = Typography;

export const EntitlementShow = () => {
  const t = useTranslate();
  const { query } = useShow<EntitlementDetailOut>({
    resource: 'monetization.entitlements',
  });
  const { data, isLoading } = query;
  const record = data?.data;
  const [addEntryOpen, setAddEntryOpen] = useState(false);
  const [prolongOpen, setProlongOpen] = useState(false);

  return (
    <Show isLoading={isLoading}>
      <Title level={4}>{t('base.sections.basic_info')}</Title>
      <Descriptions
        bordered
        column={2}
        size="small"
        style={{ marginBottom: 24 }}
      >
        <Descriptions.Item label={t('base.fields.id')}>
          {record?.id}
        </Descriptions.Item>
        <Descriptions.Item
          label={t('monetization.entitlements.fields.account_id')}
        >
          {record?.account_id ?? '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('monetization.entitlements.fields.status')}>
          <EntitlementStatusTag value={record?.status} />
        </Descriptions.Item>
        <Descriptions.Item
          label={t('monetization.entitlements.fields.valid_from')}
        >
          {record?.valid_from ? (
            <DateField value={record.valid_from} format="LLL" />
          ) : (
            '-'
          )}
        </Descriptions.Item>
        <Descriptions.Item
          label={t('monetization.entitlements.fields.valid_until')}
        >
          {record?.valid_until ? (
            <DateField value={record.valid_until} format="LLL" />
          ) : (
            '-'
          )}
        </Descriptions.Item>
      </Descriptions>

      <Divider />
      <Space
        align="center"
        style={{
          width: '100%',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}
        wrap
      >
        <Title level={4} style={{ marginBottom: 0 }}>
          {t('monetization.entitlements.sections.validity')}
        </Title>
        <Button
          type="primary"
          icon={<ForwardOutlined />}
          onClick={() => setProlongOpen(true)}
          disabled={record == null}
        >
          {t('monetization.entitlements.prolong.trigger')}
        </Button>
      </Space>

      <Descriptions bordered column={2} size="small">
        <Descriptions.Item
          label={t('monetization.entitlements.fields.valid_from')}
        >
          {record?.valid_from ? (
            <DateField value={record.valid_from} format="LLL" />
          ) : (
            '-'
          )}
        </Descriptions.Item>
        <Descriptions.Item
          label={t('monetization.entitlements.fields.valid_until')}
        >
          {record?.valid_until ? (
            <DateField value={record.valid_until} format="LLL" />
          ) : (
            '-'
          )}
        </Descriptions.Item>
      </Descriptions>

      <Divider />
      <Title level={4}>{t('monetization.entitlements.sections.usage')}</Title>

      <Descriptions bordered column={2} size="small">
        <Descriptions.Item
          label={t('monetization.entitlements.fields.limit_seconds')}
        >
          {record?.limit_seconds ?? '-'}
        </Descriptions.Item>
        <Descriptions.Item
          label={t('monetization.entitlements.fields.consumed_seconds')}
        >
          {record?.consumed_seconds ?? '-'}
        </Descriptions.Item>
        <Descriptions.Item
          label={t('monetization.entitlements.fields.limit_reached_at')}
          span={2}
        >
          {record?.limit_reached_at ? (
            <DateField value={record.limit_reached_at} format="LLL" />
          ) : (
            '-'
          )}
        </Descriptions.Item>
      </Descriptions>

      <Divider />
      <Space
        align="center"
        style={{
          width: '100%',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}
        wrap
      >
        <Title level={4} style={{ marginBottom: 0 }}>
          {t('monetization.entitlements.sections.ledger_entries')}
        </Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setAddEntryOpen(true)}
          disabled={record == null}
        >
          {t('monetization.entitlements.ledger.add.trigger')}
        </Button>
      </Space>

      <Table<LedgerEntryOut>
        dataSource={record?.ledger_entries ?? []}
        rowKey="id"
        size="small"
        pagination={false}
      >
        <Table.Column dataIndex="id" title={t('base.fields.id')} width={80} />
        <Table.Column
          dataIndex="created_at"
          title={t('monetization.entitlements.ledger.fields.created_at')}
          render={(value: string) =>
            value ? <DateField value={value} format="LLL" /> : '-'
          }
        />
        <Table.Column
          dataIndex="source"
          title={t('monetization.entitlements.ledger.fields.source')}
        />
        <Table.Column
          dataIndex="source_ref"
          title={t('monetization.entitlements.ledger.fields.source_ref')}
        />
        <Table.Column
          dataIndex="delta_seconds"
          title={t('monetization.entitlements.ledger.fields.delta_seconds')}
          render={(value: number | null) => {
            if (value == null) return '-';
            if (value > 0) {
              return (
                <Typography.Text type="success">{`+${value}`}</Typography.Text>
              );
            }
            if (value < 0) {
              return <Typography.Text type="danger">{value}</Typography.Text>;
            }
            return value;
          }}
        />
        <Table.Column
          dataIndex="reason"
          title={t('monetization.entitlements.ledger.fields.reason')}
        />
        <Table.Column
          dataIndex="created_by_admin_id"
          title={t(
            'monetization.entitlements.ledger.fields.created_by_admin_id'
          )}
          render={(value: number | null) => value ?? '-'}
        />
      </Table>

      {record != null && (
        <LedgerEntryAddModal
          entitlementId={record.id}
          open={addEntryOpen}
          onClose={() => setAddEntryOpen(false)}
        />
      )}
      {record != null && (
        <GrantEntitlementModal
          open={prolongOpen}
          onClose={() => setProlongOpen(false)}
          mode="prolong"
          initialAccountId={record.account_id}
          initialValidity={[
            dayjs(record.valid_until),
            dayjs(record.valid_until).add(7, 'day'),
          ]}
        />
      )}
    </Show>
  );
};
